// `print` — render the report and deliver it (preview / download / the print
// dialog). `required` keeps Next waiting until it was delivered. A report prints
// SAVED data; the validator keeps a print of this record behind its save.
//
// With a `file` block the report is FILED into a document repository first
// (`wizard_file`, read from the model on the server) — the repository copy is
// the record, so Next waits for it; `delivery: "none"` files without printing.

import { CheckCircle2, FileArchive, Printer } from 'lucide-react'
import { text } from '../model'
import { GOOD, banner, button, muted } from '../styles'
import { tw } from '../../testHooks'
import type { StepProps } from './common'

export default function PrintStep({ ctl, handheld }: StepProps) {
  const { step, scope, ui, busy } = ctl
  if (!step || !scope) return null
  const delivery = step.delivery || 'print_dialog'
  const files = !!step.file?.repository
  const fileOnly = files && delivery === 'none'
  const deliverWord = delivery === 'preview' ? 'Show report' : delivery === 'download' ? 'Download' : 'Print'
  const label = ui.printed
    ? (fileOnly ? 'File again' : files ? `${deliverWord} and file again` : 'Print again')
    : (fileOnly ? 'File the report' : files ? `${deliverWord} and file` : deliverWord)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {step.help && <div style={muted}>{text(step.help, scope)}</div>}
      <button type="button" style={button(handheld, !ui.printed, !!busy)} disabled={!!busy}
        onClick={() => void ctl.print()} {...tw('action', files ? 'file' : 'print')}>
        {files ? <FileArchive size={18} /> : <Printer size={18} />}
        {label}
        {!fileOnly && step.copies && step.copies > 1 ? ` (${step.copies} copies)` : ''}
      </button>
      {ui.printed && (
        <div style={banner(GOOD)}><CheckCircle2 size={16} />
          {files ? ` Filed in ${step.file?.repository}${fileOnly ? '.' : ' and sent to the printer.'}`
            : ' Sent to the printer.'}
        </div>
      )}
      {files && !ui.printed && <div style={muted}>The report is filed before you continue.</div>}
      {!files && step.required && !ui.printed && <div style={muted}>This must be printed before you continue.</div>}
    </div>
  )
}
